if (!customElements.get('image-magnifier')) {
  class ImageMagnifier extends HTMLElement {
    constructor() {
      super();
    }

    /**
     * The small visible controls drawn on top of the image. Hovering one hides
     * the lens so the icon stays readable, matching how the zoom toggle already
     * behaved.
     *
     * Note this targets the icon badges, not their parent anchors: the modal
     * opener is a full size invisible overlay covering the whole image, so
     * excluding that would suppress the lens everywhere.
     */
    static get CONTROL_SELECTOR() {
      return ['.image-magnifier__toggle', '.main-product__media-toggle-icon'].join(', ');
    }

    connectedCallback() {
      this.zoom = parseFloat(this.dataset.magnifierZoom) || 2.5;
      this.lensSize = parseInt(this.dataset.magnifierLensSize, 10) || 200;
      this.src = this.dataset.magnifierSrc;

      if (!this.src) return;

      this.imageEl = this.querySelector('img');
      this.lens = this.querySelector('.image-magnifier__lens');
      this.toggleBtn = this.querySelector('.image-magnifier__toggle');

      this.zoomActive = false;
      this._origSrc = null;
      this._origSrcset = null;

      if (this.lens) {
        this.lens.style.setProperty('--magnifier-lens-size', this.lensSize + 'px');
        this.lens.style.backgroundImage = 'url("' + this.src + '")';
      }

      this._preload = new Image();
      this._preload.src = this.src;

      this._onEnter = (e) => this.onEnter(e);
      this._onLeave = (e) => this.onLeave(e);
      this._onMove = (e) => this.onMove(e);
      this._onTouchStart = (e) => this.onTouchPan(e);
      this._onTouchMove = (e) => this.onTouchPan(e);
      this._onToggleClick = (e) => this.onToggleClick(e);

      this.addEventListener('mouseenter', this._onEnter);
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('mousemove', this._onMove);
      this.addEventListener('touchstart', this._onTouchStart, { passive: false });
      this.addEventListener('touchmove', this._onTouchMove, { passive: false });

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', this._onToggleClick);
      }

      // Inside the fullscreen viewer the whole image toggles zoom, so a click
      // zooms in and the next click zooms back out. On the product page the
      // image is not a zoom target at all - it opens the viewer instead - so
      // this is deliberately limited to viewer slides.
      this.isInsideViewer = Boolean(this.closest('.bc-gallery-modal'));

      if (this.isInsideViewer) {
        this._onImageClick = (e) => this.onImageClick(e);
        this.addEventListener('click', this._onImageClick);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('mousemove', this._onMove);
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchmove', this._onTouchMove);
      if (this.toggleBtn && this._onToggleClick) {
        this.toggleBtn.removeEventListener('click', this._onToggleClick);
      }
      if (this._onImageClick) {
        this.removeEventListener('click', this._onImageClick);
      }
    }

    /**
     * Toggles zoom from a click anywhere on the image. The zoom button has its
     * own handler and stops propagation, so it is not double-toggled here.
     */
    onImageClick(e) {
      if (this.isOverControl(e.target)) {
        return;
      }

      this.setZoomActive(!this.zoomActive);
    }

    /**
     * True when the pointer is over a control rather than the image itself.
     * The lens is suppressed there so the control stays readable, the same way
     * it already behaved for the zoom toggle.
     */
    isOverControl(target) {
      if (!target || typeof target.closest !== 'function') {
        return false;
      }

      const control = target.closest(ImageMagnifier.CONTROL_SELECTOR);

      return Boolean(control) && this.contains(control);
    }

    onEnter(e) {
      if (this.zoomActive) return;
      if (this.isOverControl(e.target)) {
        if (this.lens) this.lens.classList.remove('is-visible');
        return;
      }
      if (this.lens) this.lens.classList.add('is-visible');
      this.updateLens(e);
    }

    onLeave() {
      if (this.lens) this.lens.classList.remove('is-visible');
    }

    onMove(e) {
      if (this.isOverControl(e.target)) {
        if (this.lens) this.lens.classList.remove('is-visible');
        return;
      }
      if (this.zoomActive) {
        this.panImage(e.clientX, e.clientY);
      } else {
        if (this.lens && !this.lens.classList.contains('is-visible')) {
          this.lens.classList.add('is-visible');
        }
        this.updateLens(e);
      }
    }

    onTouchPan(e) {
      if (!this.zoomActive) return;
      if (this.isOverControl(e.target)) return;
      e.preventDefault();
      const t =
        (e.touches && e.touches[0]) ||
        (e.changedTouches && e.changedTouches[0]);
      if (!t) return;
      this.panImage(t.clientX, t.clientY);
    }

    onToggleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      this.setZoomActive(!this.zoomActive);
    }

    updateLens(e) {
      if (!this.lens || !this.imageEl) return;
      const imgRect = this.imageEl.getBoundingClientRect();
      if (imgRect.width === 0 || imgRect.height === 0) return;

      const x = e.clientX - imgRect.left;
      const y = e.clientY - imgRect.top;
      if (x < 0 || y < 0 || x > imgRect.width || y > imgRect.height) {
        this.lens.classList.remove('is-visible');
        return;
      }
      this.lens.classList.add('is-visible');

      const magRect = this.getBoundingClientRect();
      const half = this.lensSize / 2;
      const lensX = e.clientX - magRect.left - half;
      const lensY = e.clientY - magRect.top - half;
      this.lens.style.transform = 'translate(' + lensX + 'px, ' + lensY + 'px)';

      const bgW = imgRect.width * this.zoom;
      const bgH = imgRect.height * this.zoom;
      const bgX = -((x / imgRect.width) * bgW - half);
      const bgY = -((y / imgRect.height) * bgH - half);
      this.lens.style.backgroundSize = bgW + 'px ' + bgH + 'px';
      this.lens.style.backgroundPosition = bgX + 'px ' + bgY + 'px';
    }

    panImage(clientX, clientY) {
      if (!this.imageEl) return;
      const rect = this.imageEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
      const px = (x / rect.width) * 100;
      const py = (y / rect.height) * 100;
      this.imageEl.style.transformOrigin = px + '% ' + py + '%';
      this.imageEl.style.transform = 'scale(' + this.zoom + ')';
    }

    setZoomActive(active) {
      this.zoomActive = !!active;
      this.classList.toggle('is-zoom-mode', this.zoomActive);
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-pressed', String(this.zoomActive));
      }
      if (!this.imageEl) return;

      if (this.zoomActive) {
        if (this.lens) this.lens.classList.remove('is-visible');
        if (!this._origSrc) {
          this._origSrc = this.imageEl.currentSrc || this.imageEl.src;
          this._origSrcset = this.imageEl.getAttribute('srcset') || '';
          this.imageEl.removeAttribute('srcset');
          this.imageEl.src = this.src;
        }
        this.imageEl.style.transformOrigin = '50% 50%';
        this.imageEl.style.transform = 'scale(' + this.zoom + ')';
      } else {
        this.imageEl.style.transform = '';
        this.imageEl.style.transformOrigin = '';
        if (this._origSrc) {
          this.imageEl.src = this._origSrc;
          if (this._origSrcset) {
            this.imageEl.setAttribute('srcset', this._origSrcset);
          }
          this._origSrc = null;
          this._origSrcset = null;
        }
      }
    }
  }

  customElements.define('image-magnifier', ImageMagnifier);
}
